// Anonymous client for the public lap view (/lap/:raceId/:lapId). CommonApi's /public/racedata
// endpoints return the same JSON as the signed-in /racedata ones, with the driver's name cut to
// first name + last initial. Deliberately NOT a BaseClient: it never sends the auth token (the
// page is the same for everyone) and a response never logs anyone out.

import { RaceLap, TelemetryLapData } from '../models/TelemetryData';
import { ReferenceLapsResponse } from '../models/responseBodies/ReferenceLapsResponse';
import { TrackTurn } from '../utils/turnMarkers';

/** A failed public call. `status` is 0 when the request never got a response. */
export class PublicApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'PublicApiError';
  }
}

const getBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_WORLD_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_WORLD_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

async function getJson<T>(url: string, what: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    // No custom headers and no credentials: a CORS "simple" GET, so no preflight either.
    response = await fetch(url, { method: 'GET', credentials: 'omit', signal });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err;
    throw new PublicApiError(0, `Failed to load ${what} (network)`);
  }
  if (!response.ok) {
    throw new PublicApiError(response.status, `Failed to load ${what} (${response.status})`);
  }
  return (await response.json()) as T;
}

class PublicLapClient {
  /** GET {base}/public/racedata/lap/{lapId}/laps: every lap of that lap's session. */
  public getLapsForLap(lapId: string, signal?: AbortSignal): Promise<RaceLap[]> {
    if (!lapId) throw new Error('lapId is required');
    return getJson<RaceLap[]>(
      `${getBaseUrl()}public/racedata/lap/${encodeURIComponent(lapId)}/laps`, 'laps', signal);
  }

  /** GET {base}/public/racedata/telemetry/lap/{lapId}: one lap's telemetry, any venue. */
  public getLapTelemetry(lapId: string, signal?: AbortSignal): Promise<TelemetryLapData> {
    if (!lapId) throw new Error('lapId is required');
    return getJson<TelemetryLapData>(
      `${getBaseUrl()}public/racedata/telemetry/lap/${encodeURIComponent(lapId)}`, 'lap telemetry', signal);
  }

  /**
   * GET {base}/public/racedata/reference/{track}/{car}?layout=: the WOR-wide lap record
   * (`personalBest` is always null here). `layout` is sent whenever it's given, the empty
   * string included: "" is Assetto's base layout, and leaving it off would search every layout.
   */
  public getLapRecord(track: string, car: string, layout?: string, signal?: AbortSignal): Promise<ReferenceLapsResponse> {
    if (!track || !car) throw new Error('track and car are required');
    const search = layout !== undefined ? `?layout=${encodeURIComponent(layout)}` : '';
    return getJson<ReferenceLapsResponse>(
      `${getBaseUrl()}public/racedata/reference/${encodeURIComponent(track)}/${encodeURIComponent(car)}${search}`,
      'lap record', signal);
  }

  /**
   * GET {base}/public/racedata/corners/{track}/{layout}: the layout's placed official turns, or []
   * when it isn't fully mapped. The base layout ("") can't be a route segment, so it's
   * /corners/{track} with no layout at all.
   */
  public getCorners(track: string, layout?: string | null, signal?: AbortSignal): Promise<TrackTurn[]> {
    if (!track) throw new Error('track is required');
    const layoutSegment = layout ? `/${encodeURIComponent(layout)}` : '';
    return getJson<TrackTurn[]>(
      `${getBaseUrl()}public/racedata/corners/${encodeURIComponent(track)}${layoutSegment}`, 'corners', signal);
  }
}

const publicLapApi = new PublicLapClient();
export default publicLapApi;
